/* eslint-disable import/no-extraneous-dependencies */
import { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import PropTypes from "prop-types";
import { cn } from "../../lib/utils";

const tabShape = PropTypes.shape({
  label: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  disabled: PropTypes.bool
});

/**
 * Line-style tab strip (desktop).
 * Shows the first `maxVisible` tabs; any extras collapse into an "Others"
 * dropdown. When an overflow tab is the active one, it is promoted to an
 * additional visible tab after the fixed set.
 */
const LineTabsDesktop = ({
  tabs,
  value = undefined,
  onChange,
  maxVisible,
  othersLabel,
  className = ""
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const fixed = tabs.slice(0, maxVisible);
  const overflow = tabs.slice(maxVisible);
  const hasOverflow = overflow.length > 0;
  const promoted = overflow.find((t) => {
    return t.value === value;
  });

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      return document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const renderTab = (tab) => {
    const isActive = tab.value === value;
    return (
      <button
        key={tab.value}
        type='button'
        role='tab'
        aria-selected={isActive}
        disabled={tab.disabled}
        onClick={() => {
          return onChange(tab.value);
        }}
        className={cn(
          "flex items-center justify-center whitespace-nowrap px-2 py-4 -mb-px !text-[12px] leading-5 border-b-2 transition-colors cursor-pointer first:pl-0",
          "disabled:pointer-events-none disabled:opacity-50",
          isActive
            ? "border-[#343c76] text-[#343c76] font-medium"
            : "border-transparent text-[#141414] font-normal hover:text-[#343c76]"
        )}
      >
        {tab.label}
      </button>
    );
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative hidden sm:block border-b border-[#0000001f]",
        className
      )}
    >
      <div role='tablist' className='flex items-center gap-8'>
        {fixed.map(renderTab)}
        {promoted && renderTab(promoted)}

        {hasOverflow && (
          <div className='relative -mb-px'>
            <button
              type='button'
              aria-haspopup='menu'
              aria-expanded={open}
              onClick={() => {
                return setOpen((prev) => {
                  return !prev;
                });
              }}
              className={cn(
                "flex items-center justify-center gap-2 whitespace-nowrap px-2 py-4 !text-[12px] leading-5 border-b-2 border-transparent font-normal transition-colors cursor-pointer",
                open ? "text-[#343c76]" : "text-[#141414] hover:text-[#343c76]"
              )}
            >
              {othersLabel}
              <ChevronDown
                className={cn(
                  "h-6 w-6 text-[#595959] transition-transform duration-200",
                  open && "rotate-180"
                )}
              />
            </button>

            {open && (
              <div className='absolute left-0 top-[calc(100%+6px)] z-50 min-w-[220px] overflow-hidden rounded-md border border-[#d9d9d9] bg-white py-1 shadow-lg'>
                {overflow.map((tab) => {
                  const isSelected = tab.value === value;
                  return (
                    <button
                      key={tab.value}
                      type='button'
                      disabled={tab.disabled}
                      onClick={() => {
                        onChange(tab.value);
                        setOpen(false);
                      }}
                      className={cn(
                        "flex w-full items-center px-3 py-3 text-left text-[14px] leading-5 transition-colors cursor-pointer hover:bg-[#f5f3f0]",
                        "disabled:pointer-events-none disabled:opacity-50",
                        isSelected
                          ? "font-medium text-[#927940]"
                          : "text-[#262626]"
                      )}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

LineTabsDesktop.propTypes = {
  tabs: PropTypes.arrayOf(tabShape).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  maxVisible: PropTypes.number.isRequired,
  othersLabel: PropTypes.node.isRequired,
  className: PropTypes.string
};

/**
 * Mobile variant: a heading + the current selection, which opens a
 * "Switch View" bottom-sheet listing every tab.
 */
const LineTabsMobile = ({
  tabs,
  value = undefined,
  onChange,
  heading = null,
  drawerTitle,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [show, setShow] = useState(false);

  const active = tabs.find((t) => {
    return t.value === value;
  });
  const currentLabel = (active ?? tabs[0])?.label;

  const openSheet = () => {
    setIsOpen(true);
  };

  const closeSheet = () => {
    setShow(false);
    setTimeout(() => {
      setIsOpen(false);
    }, 300);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const id = setTimeout(() => {
      setShow(true);
    }, 10);
    return () => {
      return clearTimeout(id);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleSelect = (v) => {
    onChange(v);
    closeSheet();
  };

  return (
    <div
      className={cn(
        "sm:hidden py-3 border-b border-[#e8e2d8]",
        className
      )}
    >
      <div
        className={cn(
          "flex items-center",
          heading ? "justify-between" : "justify-end"
        )}
      >
        {heading && (
          <span className='text-[20px] font-semibold leading-7 text-[#141414]'>
            {heading}
          </span>
        )}
        <button
          type='button'
          onClick={openSheet}
          className='flex items-center gap-1 shrink-0 cursor-pointer'
        >
          <span className='max-w-[160px] truncate text-[14px] font-semibold leading-5 text-[#343c76]'>
            {currentLabel}
          </span>
          {isOpen ? (
            <ChevronUp className='h-[18px] w-[18px] shrink-0 text-[#343c76]' />
          ) : (
            <ChevronDown className='h-[18px] w-[18px] shrink-0 text-[#343c76]' />
          )}
        </button>
      </div>

      {isOpen && (
        <div className='fixed inset-0 z-[9999] flex flex-col justify-end'>
          <div
            role='button'
            tabIndex={0}
            aria-label='Close view selector'
            className={cn(
              "absolute inset-0 bg-black/40 transition-opacity duration-300",
              show ? "opacity-100" : "opacity-0"
            )}
            onClick={closeSheet}
            onKeyDown={(e) => {
              if (e.key === "Escape" || e.key === "Enter") closeSheet();
            }}
          />

          <div
            className={cn(
              "relative rounded-t-[24px] bg-white drop-shadow-[0px_0px_3.5px_rgba(0,0,0,0.25)] transition-transform duration-300 ease-out",
              show ? "translate-y-0" : "translate-y-full"
            )}
          >
            {/* Close button sits 2rem (32px) above the drawer: 40px btn + 32px gap = 72px */}
            <button
              type='button'
              aria-label='Close'
              onClick={closeSheet}
              className='absolute -top-[72px] left-1/2 flex h-10 w-10 min-h-10 min-w-10 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full bg-white shadow-[0px_0px_3.5px_rgba(0,0,0,0.25)] box-border p-0'
            >
              <X className='h-5 w-5 text-[#141414]' />
            </button>

            <div className='h-[14px]' />

            <div className='flex items-center px-5 py-4'>
              <span className='text-[16px] font-medium leading-6 text-[#141414]'>
                {drawerTitle}
              </span>
            </div>

            <div className='h-px bg-[#d9d9d9]' />

            <div className='flex flex-col gap-1 px-5 pb-5 pt-3'>
              {tabs.map((tab) => {
                const isSelected = tab.value === value;
                return (
                  <button
                    key={tab.value}
                    type='button'
                    disabled={tab.disabled}
                    onClick={() => {
                      return handleSelect(tab.value);
                    }}
                    className='w-full rounded-md px-2 py-3 text-left transition-colors active:bg-[#f5f3f0] disabled:pointer-events-none disabled:opacity-50'
                  >
                    <span
                      className={cn(
                        "text-[14px] font-medium leading-5",
                        isSelected ? "text-[#927940]" : "text-[#262626]"
                      )}
                    >
                      {tab.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

LineTabsMobile.propTypes = {
  tabs: PropTypes.arrayOf(tabShape).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  heading: PropTypes.node,
  drawerTitle: PropTypes.node.isRequired,
  className: PropTypes.string
};

/**
 * LineTabs — a generalized, controlled tab strip.
 *
 * Desktop: `maxVisible` line tabs plus an "Others" dropdown for the rest;
 * a selected overflow tab is promoted to a visible tab.
 * Mobile: a heading + current-selection link that opens a "Switch View" sheet.
 *
 * Controlled via `value` + `onChange`.
 */
const LineTabs = ({
  tabs,
  value = undefined,
  onChange,
  maxVisible = 4,
  othersLabel = "Others",
  heading = null,
  drawerTitle = "Switch View",
  className = ""
}) => {
  if (!tabs || tabs.length === 0) return null;

  return (
    <>
      <LineTabsDesktop
        tabs={tabs}
        value={value}
        onChange={onChange}
        maxVisible={maxVisible}
        othersLabel={othersLabel}
        className={className}
      />
      <LineTabsMobile
        tabs={tabs}
        value={value}
        onChange={onChange}
        heading={heading}
        drawerTitle={drawerTitle}
        className={className}
      />
    </>
  );
};

LineTabs.propTypes = {
  /** Array of tab descriptors. */
  tabs: PropTypes.arrayOf(tabShape).isRequired,
  /** Currently active tab value (controlled). */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Called with the selected tab's value. */
  onChange: PropTypes.func.isRequired,
  /** Number of tabs shown before overflowing into "Others". */
  maxVisible: PropTypes.number,
  /** Label for the overflow dropdown trigger. */
  othersLabel: PropTypes.node,
  /** Optional heading shown on the mobile row. */
  heading: PropTypes.node,
  /** Title of the mobile bottom-sheet. */
  drawerTitle: PropTypes.node,
  /** Extra classes applied to both the desktop and mobile roots (use responsive prefixes for per-breakpoint padding). */
  className: PropTypes.string
};

export { LineTabs };
